import React from "react";
import moment from "moment";
import { Link } from "react-router";
import { Checkbox, RaisedButton, Popover, Menu, MenuItem } from "material-ui";
import { Table, Tr, Td, Sort } from "reactable";
import CheckIcon from "material-ui/svg-icons/action/check-circle";

import TransferOverlay from "./overlays/transfer-overlay.jsx";
import PrintPassesModal from "./print-passes-modal.jsx";
import SupplementaryDataModal from "./supplementary-data-modal.jsx";
import PassSubscriptionModal from "./pass-subscription-modal.jsx";
import PassNameEditor from "./pass-name-editor.jsx";
import PassPassBucketEditor from "./pass-pass-bucket-editor.jsx";
import InlineLinkIconButton from "./inline-link-icon-button.jsx";
import LinkLike from "./link-like.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import UserStore from "../../stores/UserStore";
import PassStore from "../../stores/PassStore";
import PassBucketStore from "../../stores/PassBucketStore";
import PassBucketActions from "../../actions/PassBucketActions";
import PassActions from "../../actions/PassActions";
import BulkDeleteOverlay from "./overlays/bulk-delete-overlay.jsx";
import OrderUtil from "../../utils/OrderUtil.js";
import RefundOverlay from "./overlays/refund.jsx";
import LoadingOverlay from "./overlays/loading.jsx";

const PAGE_SIZE = 20;

const DELETED_ROW_STYLE = { textDecoration: "line-through", color: "#ccc" };
const HIGHLIGHTED_ROW_STYLE = {
  backgroundColor: "#fff176",
  boxShadow: "1px 1px 10px rgba(0,0,0,0.2)"
};

class PassTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isEditing: false,
      fetchedBucket: null,
      selectedPasses: {},
      passesToDelete: null,
      passesToTransfer: null,
      currentlyPrinting: null,
      currentlyTransferring: null,
      highlightedRow: null,
      lastPassSubscription: null,
      currentPage: 0
    };

    this._makeRowForPass = this._makeRowForPass.bind(this);
    this._onEditCheck = this._onEditCheck.bind(this);
    this._onCheckAll = this._onCheckAll.bind(this);
    this._onCheck = this._onCheck.bind(this);

    this._closePrintModal = this._closePrintModal.bind(this);
    this._closeSupplementaryData = this._closeSupplementaryData.bind(this);

    this._closeTransferModal = this._closeTransferModal.bind(this);
    this._submitTransfer = this._submitTransfer.bind(this);

    this._showDeleteModal = this._showDeleteModal.bind(this);
    this._closeDeleteModal = this._closeDeleteModal.bind(this);
    this._onDeletePasses = this._onDeletePasses.bind(this);

    this._showRefundModal = this._showRefundModal.bind(this);
    this._closeRefundModal = this._closeRefundModal.bind(this);
    this._onRefundPasses = this._onRefundPasses.bind(this);
    this._closeRefundStatusModal = this._closeRefundStatusModal.bind(this);

    this._showBulkTransferModal = this._showBulkTransferModal.bind(this);
    this._submitBulkTransfer = this._submitBulkTransfer.bind(this);

    this._manageSubscription = this._manageSubscription.bind(this);
    this._closeSubscription = this._closeSubscription.bind(this);
  }

  static getStores() {
    return [PassBucketStore, PassStore];
  }

  static getPropsFromStores() {
    let state = PassBucketStore.getState();
    state.getPassSubscriptionById = PassStore.getPassSubscriptionById;
    return state;
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  _delete(id, name, e) {
    e.preventDefault();
    e.stopPropagation();

    if (
      window.confirm(
        `Are you sure you want to delete ${this.props.isMemberships ? "membership" : "pass"} "${(name || id)}"? There is no going back.`
      )
    ) {
      PassActions.delete(id);
      this.setState(this.state);
    }
  }

  _print(id, e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ currentlyPrinting: id });
  }
  _closePrintModal() {
    this.setState({ currentlyPrinting: null });
  }

  _reserve(id, eventBucket, e) {
    e.preventDefault();
    e.stopPropagation();

    this.context.router.push(
      `/event_buckets/${eventBucket}/box_office?pass=${id}`
    );
  }

  _viewHistory(id, eventBucket, e) {
    e.preventDefault();
    e.stopPropagation();
    this.context.router.push(
      `/event_buckets/${eventBucket}/passes/${id}/history`
    );
  }

  _onCheckAll() {
    if (
      Object.keys(this.state.selectedPasses).length === this.props.passes.length
    ) {
      this.setState({ selectedPasses: {} });
    } else {
      this.setState({
        selectedPasses: this.props.passes.reduce((d, t) => {
          d[t.id] = true;
          return d;
        }, {})
      });
    }
  }

  _onCheck(pass) {
    if (!this.state.selectedPasses[pass.id]) {
      this.state.selectedPasses[pass.id] = true;
    } else {
      delete this.state.selectedPasses[pass.id];
    }
    this.setState({ selectedPasses: this.state.selectedPasses });
  }

  _transfer(id, e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ currentlyTransferring: id });
  }

  _submitTransfer(recipient) {
    PassActions.transfer(this.state.currentlyTransferring, recipient);
    this._closeTransferModal();
  }

  _cancelTransfer(id, e) {
    e.preventDefault();
    e.stopPropagation();
    if (
      window.confirm(
        "Are you sure you wish to cancel this pass transfer? The original transfer link will no longer work."
      )
    ) {
      PassActions.cancelTransfer(id);
    }
  }

  _bulkCancelTransfer(passes) {
    this.setState({
      bulkActionsPopoverOpen: false
    });
    const plurality = passes.length == 1 ? "" : "s";

    if (
      window.confirm(
        `Are you sure you wish to cancel ${passes.length} pass transfer${plurality}? The original transfer link${plurality} will no longer work.`
      )
    ) {
      const passIds = passes.map(p => p.id);
      PassActions.bulkCancelPassTransfers(passIds);
    }
  }

  _closeTransferModal() {
    this.setState({ currentlyTransferring: null, passesToTransfer: null });
  }

  _showBulkTransferModal() {
    this.setState({
      bulkActionsPopoverOpen: false
    });
    const passesToTransfer = this.passesEligibleForTransfer(
      this.state.selectedPasses
    );

    this.setState({
      passesToTransfer
    });
  }

  _submitBulkTransfer(recipient) {
    const transfer = {
      destination_email: recipient,
      pass_ids: this.state.passesToTransfer.map(p => p.id)
    };
    PassActions.bulkTransferPasses(transfer);

    this._closeTransferModal();
    this.setState({
      selectedPasses: {}
    });
  }

  _showDeleteModal(passId) {
    this.setState({
      bulkActionsPopoverOpen: false
    });
    const passesToDelete = passId
      ? [passId]
      : Object.keys(this.state.selectedPasses);

    this.setState({
      passesToDelete: passesToDelete.map(id =>
        this.props.passes.find(t => t.id === id)
      )
    });
  }

  _closeDeleteModal() {
    this.setState({ passesToDelete: null });
  }

  _onDeletePasses() {
    const passIds = this.state.passesToDelete.map(t => t.id);
    PassActions.deletePasses({
      pass_ids: passIds
    });

    this._closeDeleteModal();
    this.setState({
      selectedPasses: {}
    });
  }

  async _showRefundModal() {
    this.setState({ bulkActionsPopoverOpen: false });
    const passIdsToRefund = Object.keys(this.state.selectedPasses);
    const passesToRefund = passIdsToRefund.map(id =>
      this.props.passes.find(p => p.id === id)
    );

    // deduplicate the orders list
    const ordersToRefund = Array.from(
      new Set(passesToRefund.map(p => p.order || this.props.order))
    );

    const apiKey = UserStore.getState().apiKey;

    // get the refund options for all of the orders
    const refundOptions = await Promise.all(
      ordersToRefund.map(async orderId => {
        try {
          return await OrderUtil.generateRefundOptions(apiKey, orderId);
        } catch (error) {
          return { error };
        }
      })
    );

    // merge the options into one array
    const mergedRefundOptions = refundOptions.reduce((result, obj) => {
      return result.concat(obj.options);
    }, []);

    // filter out only the relevant refund options to the passes selected
    const filteredRefundOpts = mergedRefundOptions.filter(option => {
      if (!option || option.item.ref_type !== "Pass") return false;

      return passIdsToRefund.includes(option.item.ref_id);
    });

    this.setState({
      passesToRefund: passesToRefund,
      refundOptions: filteredRefundOpts
    });
  }

  async _onRefundPasses(options) {
    const optionsByOrder = {};
    for (const key in options.refunds) {
      const keySplit = key.split("-");
      const item = options.refunds[key];
      const id = keySplit[1];

      const pass = this.props.passes.find(t => t.id === id);
      const order = pass.order || this.props.order;

      if (!optionsByOrder[order]) {
        optionsByOrder[order] = {};
      }

      optionsByOrder[order][key] = item;
    }

    this.setState({
      passesToRefund: null,
      refundOptions: null,
      bulkRefunding: true,
      bulkRefundingOrderCount: Object.keys(optionsByOrder).length,
      bulkRefundingProgress: 0
    });

    let progress = 0;
    for (const orderId in optionsByOrder) {
      try {
        progress += 1;

        this.setState({
          bulkRefundingProgress: progress
        });

        PassActions.refundPass({ orderId, options: optionsByOrder[orderId] });
      } catch (err) {
        console.error(err);
        console.error(`refund on order ${orderId} failed`);
      }
    }

    this.setState({
      bulkRefundingComplete: true
    });
  }

  _closeRefundModal() {
    this.setState({
      passesToRefund: null,
      refundOptions: null
    });
  }

  _closeRefundStatusModal() {
    this.setState({
      passesToRefund: null,
      refundOptions: null,
      bulkRefunding: false,
      bulkRefundingOrderCount: 0,
      bulkRefundingProgress: 0
    });
  }

  _changeAutorenewalStatus(id, shouldAutorenew) {
    PassActions.editPassSubscription(id, {
      should_autorenew: shouldAutorenew,
      silent: true
    });
  }

  _manageSubscription(id) {
    this.context.router.push({
      pathname: this.props.location.pathname,
      query: { pass_subscription: id },
      state: { ignoreScrollBehavior: true }
    });
  }

  _closeSubscription() {
    this.context.router.goBack();
  }

  _onEditCheck(e) {
    this.setState({ isEditing: !this.state.isEditing });
  }

  _showSupplementaryData(pass, e) {
    e.preventDefault();
    e.stopPropagation();
    this.context.router.push({
      pathname: this.props.location.pathname,
      query: { pass: pass.id },
      state: { ignoreScrollBehavior: true }
    });
  }

  _closeSupplementaryData() {
    this.context.router.goBack();
  }

  componentDidMount() {
    this.componentWillReceiveProps(this.props);
  }

  componentWillReceiveProps(props) {
    if (
      props.location &&
      props.location.query.highlight_pass &&
      this.table &&
      this.table.data.length
    ) {
      let rowLocation = this.table.data.findIndex(
          p => p.data["Id"].value === props.location.query.highlight_pass
        ),
        neededPage = Math.floor(rowLocation / PAGE_SIZE);

      this.setState({
        highlightedId: props.location.query.highlight_pass,
        currentPage: neededPage
      });
    }

    if (props.eventBucket && props.eventBucket !== this.state.fetchedBucket) {
      this.setState(
        {
          fetchedBucket: props.eventBucket
        },
        () => {
          PassBucketActions.fetchAllForEventBucket.defer(props.eventBucket);
        }
      );
    }

    if (
      props.location.query.pass_subscription &&
      props.location.query.pass_subscription !== this.state.lastPassSubscription
    ) {
      PassActions.fetchPassSubscription.defer(
        props.location.query.pass_subscription
      );
      this.setState({
        lastPassSubscription: props.location.query.pass_subscription
      });
    }
  }

  componentDidUpdate() {
    if (
      this.highlightedRow &&
      this.state.highlightedRow !== this.highlightedRow
    ) {
      this.setState(
        {
          highlightedRow: this.highlightedRow
        },
        () => {
          setTimeout(() => {
            if (
              this.highlightedRow &&
              this.highlightedRow.scrollIntoViewIfNeeded
            ) {
              // chrome only
              this.highlightedRow.scrollIntoViewIfNeeded();
            } else if (this.highlightedRow) {
              this.highlightedRow.scrollIntoView();
            }
          }, 0);
        }
      );
    }
  }

  _makeRowForPass(passBuckets, eventBucketId, showTransferHistory, pass) {
    let isDeleted = PassStore.getState().deletedPasses[pass.id],
      isSelected =
        this.state.selectedPasses && this.state.selectedPasses[pass.id],
      shouldHighlight = this.state.highlightedId === pass.id;
    const onCheck = this._onCheck.bind(this, pass);

    // Inject person if needed
    if ((!pass.person || !pass.person.email) && this.props.person) {
      pass.person = this.props.person;
    }

    const passEventBucketId = typeof pass.event_bucket === "object" ? pass.event_bucket.id : pass.event_bucket

    return (
      <Tr
        style={Object.assign(
          {},
          isDeleted && DELETED_ROW_STYLE,
          shouldHighlight && HIGHLIGHTED_ROW_STYLE
        )}
        key={pass.id}
      >
        <Td column=" " value={pass.id}>
          <div onClick={onCheck}>
            <Checkbox label=" " checked={isSelected} />
          </div>
        </Td>
        <Td column="Id">{pass.id}</Td>
        <Td column="Check-In" value={pass.checked_in_at}>
          <span
            onTouchTap={() =>
              this.context.router.push({
                pathname: `/event_buckets/${passEventBucketId}/check_in`,
                state: { selectedPassId: pass.id }
              })
            }
            title={
              pass.checked_in_at
                ? `Checked in ${moment(pass.checked_in_at).format("lll")}`
                : "Not yet checked in"
            }
            style={{ marginLeft: 0, marginRight: 0, cursor: "pointer" }}
          >
            <CheckIcon
              style={{
                color: pass.checked_in_at ? "#4caf50" : "rgba(0,0,0,0.1)",
                width: 20,
                height: 20,
                verticalAlign: -6
              }}
            />
          </span>
        </Td>
        <Td
          column="Person"
          value={
            pass.person
              ? pass.person.name || pass.person.email
              : "Unknown Person"
          }
        >
          {pass.person ? (
            <div>
              {pass.person.name || pass.person.email}
              <InlineLinkIconButton
                onTouchTap={() => {
                  this.context.router.push(
                    `/event_buckets/${eventBucketId}/people/${pass.person.id}`
                  );
                }}
                tooltip="View person"
              />
            </div>
          ) : (
            "Unknown Person"
          )}
        </Td>
        <Td column="Name" value={pass.name}>
          <PassNameEditor pass={pass} showEditButton={this.state.isEditing} isMembership={this.props.isMemberships} />
        </Td>
        <Td column="Pass Bucket">
          <PassPassBucketEditor
            linkStyle={isDeleted && { color: "#ccc" }}
            pass={pass}
            passBuckets={passBuckets}
            showEditButton={this.state.isEditing}
          />
        </Td>
        <Td column="Uses">
          {pass.uses.length + (pass.uses_passes ? pass.uses_passes.length : 0)}
        </Td>
        <Td column="Order Date" value={pass.created_at}>
          <div>
            {moment(pass.created_at).format("lll")}
            {this.props.order || pass.order ? (
              <InlineLinkIconButton
                onTouchTap={() => {
                  this.context.router.push(
                    `/event_buckets/${eventBucketId}/orders/${
                      this.props.order || pass.order
                    }`
                  );
                }}
                tooltip="View order"
              />
            ) : null}
          </div>
        </Td>
        {pass.pass_subscription ? (
          <Td
            column="Renewal Date"
            value={pass.pass_subscription.renewal_date}
            style={
              pass.pass_subscription.status !== "active"
                ? { textDecoration: "line-through" }
                : {}
            }
          >
            {moment(pass.pass_subscription.renewal_date).format("ll")}
          </Td>
        ) : null}
        {pass.pass_subscription ? (
          <Td column="Autorenewal Status">
            <span>
              {pass.pass_subscription.status === "active"
                ? pass.pass_subscription.should_autorenew
                  ? "Enabled"
                  : "Disabled"
                : pass.pass_subscription.expired_date ||
                  pass.pass_subscription.status === "expired"
                ? "Expired"
                : "Canceled"}
              &nbsp;
              {UserStore.hasPermission("edit_pass") ? (
                <div>
                  (
                  <LinkLike
                    onTouchTap={() =>
                      this._manageSubscription(pass.pass_subscription.id)
                    }
                  >
                    manage
                  </LinkLike>
                  )
                </div>
              ) : null}
            </span>
          </Td>
        ) : null}
        {pass.pass_bucket &&
        pass.pass_bucket.supplementary_data &&
        UserStore.hasPermission("edit_pass") ? (
          <Td column="Data">
            <a href="#" onClick={this._showSupplementaryData.bind(this, pass)}>
              View & edit
            </a>
          </Td>
        ) : null}
        <Td column="Actions">
          <div>
            <div
              ref={r => {
                if (shouldHighlight) {
                  this.highlightedRow = r;
                }
              }}
            />
            {pass.pending_transfer_recipient &&
            UserStore.hasPermission("manage_orders") ? (
              <span>
                <i
                  title={`Pending transfer to ${pass.pending_transfer_recipient}`}
                >
                  Pending&nbsp;
                  <small>
                    [
                    <a
                      style={isDeleted && { color: "#ccc" }}
                      href="#"
                      onClick={this._cancelTransfer.bind(this, pass.id)}
                    >
                      Cancel
                    </a>
                    ]
                  </small>
                </i>
              </span>
            ) : UserStore.hasPermission("manage_orders") ? (
              <a
                style={isDeleted && { color: "#ccc" }}
                href="#"
                onClick={this._transfer.bind(this, pass.id)}
              >
                Transfer
              </a>
            ) : null}
            &nbsp;
            {UserStore.hasPermission("manage_orders") ? (
              <a
                style={isDeleted && { color: "#ccc" }}
                href="#"
                onClick={this._print.bind(this, pass.id)}
              >
                Print
              </a>
            ) : null}
            &nbsp;
            <a
              title="New passholder reservation"
              style={isDeleted && { color: "#ccc" }}
              href="#"
              onClick={this._reserve.bind(this, pass.id, passEventBucketId)}
            >
              Use
            </a>
            &nbsp;
            {UserStore.hasPermission("manage_orders") && (
              <a
                style={isDeleted && { color: "#ccc" }}
                href="#"
                onClick={this._delete.bind(this, pass.id, pass.name)}
              >
                Delete
              </a>
            )}
            &nbsp;
            {UserStore.isGod() || showTransferHistory ? (
              <a
                style={isDeleted && { color: "#ccc" }}
                href="#"
                onClick={this._viewHistory.bind(this, pass.id, this.props.eventBucket)}
              >
                View History
              </a>
            ) : null}
            &nbsp;
          </div>
        </Td>
      </Tr>
    );
  }

  passIndexToArray(passesIndex) {
    return Object.keys(passesIndex).map(id =>
      this.props.passes.find(t => t.id === id)
    );
  }

  passesEligibleForTransfer(passesIndex) {
    const passes = this.passIndexToArray(passesIndex);

    const passesEligible = passes.filter(p => !p.pending_transfer_recipient);

    return passesEligible;
  }

  passesEligibleForTransferCancel(passesIndex) {
    const passes = this.passIndexToArray(passesIndex);

    const passesEligible = passes.filter(p => !!p.pending_transfer_recipient);

    return passesEligible;
  }

  render() {
    const isSundance = this.props.isSundance;
    const showTransferHistory = this.props.showTransferHistory;

    let passBuckets = PassBucketStore.getPassBucketsForEventBucket(
        this.props.eventBucket
      ),
      hasSubscriptions = this.props.passes.some(p => p.pass_subscription),
      hasSupplementaryData = this.props.passes.some(
        p => p.pass_bucket && p.pass_bucket.supplementary_data
      ),
      hasCheckIns = this.props.passes.some(p => p.checked_in_at),
      hasInactivePasses = this.props.passes.some(p => p.is_active === false),
      rows = this.props.passes.map(
        this._makeRowForPass.bind(this, passBuckets, this.props.eventBucket, isSundance || showTransferHistory)
      ),
      styles = {
        config: {
          borderBottom: "1px solid #ccc",
          marginTop: 10,
          paddingBottom: 10,
          marginBottom: 10
        }
      },
      openedSupplementaryDataPass =
        this.props.location.query.pass &&
        this.props.passes.find(p => p.id === this.props.location.query.pass);

    const selectedPassCount = Object.keys(this.state.selectedPasses).length;
    const { passesToDelete, passesToTransfer, passesToRefund, refundOptions } =
      this.state;
    const { passes, eventBucket } = this.props;

    const selectedPassesEligibleForTransfer = this.passesEligibleForTransfer(
      this.state.selectedPasses
    );
    const selectedPassesEligibleForTransferCancel =
      this.passesEligibleForTransferCancel(this.state.selectedPasses);
    const passPluralized = "pass" + (selectedPassCount === 1 ? "" : "es");

    return (
      <div>
        {!!passesToRefund ? (
          <RefundOverlay
            options={refundOptions}
            onRefund={this._onRefundPasses}
            refundErrorMessage={this.state.refundErrorMessage}
            onRequestClose={this._closeRefundModal}
          />
        ) : null}
        {this.state.bulkRefunding ? (
          <LoadingOverlay
            onRequestClose={this._closeRefundStatusModal}
            message={
              this.state.bulkRefundingComplete
                ? `Refunds complete!`
                : `Refunding ${this.state.bulkRefundingProgress}/${this.state.bulkRefundingOrderCount} orders`
            }
            complete={this.state.bulkRefundingComplete}
          />
        ) : null}
        {!!passesToDelete ? (
          <BulkDeleteOverlay
            submitting={this.props.isFetching}
            items={passesToDelete}
            itemName={`pass${passesToDelete.length !== 1 ? "es" : ""}`}
            eventBucketId={
              eventBucket || (passes[0] ? passes[0].event.event_bucket : "")
            }
            onClose={this._closeDeleteModal}
            onDelete={this._onDeletePasses}
          />
        ) : null}
        {this.state.currentlyTransferring ? (
          <TransferOverlay
            onRequestClose={this._closeTransferModal}
            onTransfer={this._submitTransfer}
            type="pass"
            typeName={this.props.isMemberships ? `membership${
              selectedPassesEligibleForTransfer.length !== 1 ? "s" : ""
            }` : `pass${
              selectedPassesEligibleForTransfer.length !== 1 ? "es" : ""
            }`}
          />
        ) : null}

        {!!passesToTransfer ? (
          <TransferOverlay
            onRequestClose={this._closeTransferModal}
            onTransfer={this._submitBulkTransfer}
            bulkCount={selectedPassesEligibleForTransfer.length}
            typeName={this.props.isMemberships ? `membership${
              selectedPassesEligibleForTransfer.length !== 1 ? "s" : ""
            }` : `pass${
              selectedPassesEligibleForTransfer.length !== 1 ? "es" : ""
            }`}
            type="pass"
          />
        ) : null}
        {this.state.currentlyPrinting ? (
          <PrintPassesModal
            id={this.state.currentlyPrinting}
            eventBucket={this.props.eventBucket}
            requestClose={this._closePrintModal}
            isMembership={this.props.isMemberships}
          />
        ) : null}
        {openedSupplementaryDataPass ? (
          <SupplementaryDataModal
            pass={openedSupplementaryDataPass}
            person={this.props.person}
            requestClose={this._closeSupplementaryData}
          />
        ) : null}
        {this.props.location.query.pass_subscription ? (
          <PassSubscriptionModal
            passSubscription={this.props.getPassSubscriptionById(
              this.props.location.query.pass_subscription
            )}
            person={this.props.person}
            requestClose={this._closeSubscription}
          />
        ) : null}
        {hasInactivePasses ? (
          <div style={{ fontStyle: "italic" }}>
            Note: Passes marked as{" "}
            <span
              style={{
                backgroundColor: "red",
                borderRadius: 5,
                padding: "2px 4px",
                color: "white"
              }}
            >
              Deactivated
            </span>{" "}
            are not allowed to place new orders.
          </div>
        ) : null}
        {UserStore.hasPermission("edit_pass") ? (
          <div style={styles.config}>
            <Checkbox
              checked={this.state.isEditing}
              label="Enter edit mode"
              onCheck={this._onEditCheck}
            />
          </div>
        ) : null}

        <div
          style={
            this.props.order || this.props.person
              ? { float: "right", marginBottom: -36, marginRight: "-1em" }
              : { position: "absolute", right: 40, marginTop: 18 }
          }
        >
          {rows.length && selectedPassCount > 0 ? (
            <div style={{ display: "inline-block" }}>
              <RaisedButton
                label={`Bulk Actions (${selectedPassCount})`}
                style={{ marginRight: "1em" }}
                onTouchTap={e => {
                  e.preventDefault();
                  this.setState({
                    bulkActionsPopoverOpen: true,
                    bulkActionsPopoverAnchorEl: e.currentTarget
                  });
                }}
              />
              <Popover
                open={this.state.bulkActionsPopoverOpen}
                anchorEl={this.state.bulkActionsPopoverAnchorEl}
                onRequestClose={() =>
                  this.setState({ bulkActionsPopoverOpen: false })
                }
              >
                <Menu desktop={true}>
                  <MenuItem
                    disabled={
                      selectedPassesEligibleForTransferCancel.length === 0
                    }
                    primaryText={`Cancel ${
                      selectedPassesEligibleForTransferCancel.length
                    } pass transfer${
                      selectedPassesEligibleForTransferCancel.length === 1
                        ? ""
                        : "s"
                    }`}
                    onTouchTap={() =>
                      this._bulkCancelTransfer(
                        selectedPassesEligibleForTransferCancel
                      )
                    }
                    style={{ marginRight: "1em" }}
                  />
                  <MenuItem
                    disabled={selectedPassCount === 0}
                    primaryText={`Refund ${selectedPassCount} ${passPluralized}`}
                    onTouchTap={() => this._showRefundModal()}
                    style={{ marginRight: "1em" }}
                  />
                  <MenuItem
                    disabled={selectedPassesEligibleForTransfer.length === 0}
                    primaryText={`Transfer ${
                      selectedPassesEligibleForTransfer.length
                    } pass${
                      selectedPassesEligibleForTransfer.length === 1 ? "" : "es"
                    }`}
                    onTouchTap={() => this._showBulkTransferModal()}
                    style={{ marginRight: "1em" }}
                  />
                  <MenuItem
                    primaryText={`Delete ${selectedPassCount} pass${
                      selectedPassCount === 1 ? "" : "es"
                    }`}
                    onTouchTap={() => this._showDeleteModal()}
                    style={{ marginRight: "1em" }}
                  />
                </Menu>
              </Popover>
            </div>
          ) : null}

          {this.props.order || this.props.person ? (
            <RaisedButton
              style={{ marginRight: "1em" }}
              onTouchTap={this._print.bind(
                this,
                this.props.passes.map(p => p.id)
              )}
              label="Print all"
            />
          ) : null}
        </div>
        <Table
          ref={r => {
            this.table = r;
          }}
          className={
            "bordered striped responsive-table pass-table " +
            (this.state.isEditing ? "editing" : "not-editing")
          }
          sortable={[
            { column: "Order Date", sortFunction: Sort.Date },
            { column: "Name", sortFunction: Sort.CaseInsensitive },
            { column: "Person", sortFunction: Sort.CaseInsensitive },
            "Pass Bucket",
            "Uses",
            "Renewal Date"
          ]}
          defaultSort={{ column: "Order Date", direction: "desc" }}
          columns={(rows.length
            ? [
                {
                  key: " ",
                  label: (
                    <Checkbox
                      iconStyle={{ fill: "#fff" }}
                      checked={selectedPassCount === rows.length}
                      onCheck={this._onCheckAll}
                    />
                  )
                }
              ]
            : []
          )
            .concat(
              hasCheckIns
                ? [
                    {
                      key: "Check-In",
                      label: (
                        <CheckIcon
                          style={{
                            color: "#fff",
                            width: 20,
                            height: 20,
                            verticalAlign: -6
                          }}
                        />
                      )
                    }
                  ]
                : []
            )
            .concat(["Name"])
            .concat(this.props.isSinglePerson ? [] : ["Person"])
            .concat(
              this.props.hidePassBucket && !this.state.isEditing
                ? []
                : [{key: "Pass Bucket", label: this.props.isMemberships ? "Membership Bucket" : "Pass Bucket"}]
            )
            .concat(["Uses", "Order Date"])
            .concat(
              hasSubscriptions ? ["Renewal Date", "Autorenewal Status"] : []
            )
            .concat(
              hasSupplementaryData &&
                UserStore.hasPermission("edit_pass_bucket")
                ? ["Data"]
                : []
            )
            .concat(["Actions"])}
          filterable={["Name", "Person", "Pass Bucket"]}
          filterPlaceholder="Type to filter..."
          itemsPerPage={rows.length > PAGE_SIZE ? PAGE_SIZE : undefined}
          currentPage={this.state.currentPage}
          onPageChange={p => this.setState({ currentPage: p })}
        >
          {rows}
        </Table>
        {rows.length === 0 && !this.props.hideNoPassesMessage ? (
          <div className="empty-table-message">No {this.props.isMemberships ? "memberships" : "passes"} yet.</div>
        ) : null}
      </div>
    );
  }
}

export default connectToStores(PassTable);



// WEBPACK FOOTER //
// ./src/app/components/partials/pass-table.jsx